import React, { useEffect, useState } from 'react';
import type { SubmitStatus } from '../types/api';
import StatusAlert from './StatusAlert';

type Language = 'en' | 'es';

interface Translations {
  thankYouTitle: string;
  thankYouDescription: string;
  thankYouDescriptionAfterEmail: string;
  emailLabel: string;
  emailPlaceholder: string;
  submitButton: string;
  submittingButton: string;
  submitSuccess: string;
  emailAlreadySent: string;
  submitError: string;
  invalidEmail: string;
  restartButton: string;
  closeMessage: string;
}

interface TranslationsMap {
  en: Translations;
  es: Translations;
}

const translations: TranslationsMap = {
  en: {
    thankYouTitle: 'Thank you for completing the survey!',
    thankYouDescription: 'We appreciate your feedback. If you would like follow-up information about this survey, you can share your email below.',
    thankYouDescriptionAfterEmail: 'Thank you for sharing your email. We will contact you later with survey results and any important updates.',
    emailLabel: 'Email address',
    emailPlaceholder: 'your@email.com',
    submitButton: 'Submit',
    submittingButton: 'Submitting...',
    submitSuccess: 'Email updates have been enabled. Thank you for your interest.',
    emailAlreadySent: 'Email updates are already enabled for this survey.',
    submitError: 'We could not save your email right now. Please try again.',
    invalidEmail: 'Please enter a valid email address',
    restartButton: 'New Survey',
    closeMessage: 'Close message',
  },
  es: {
    thankYouTitle: '¡Gracias por completar la encuesta!',
    thankYouDescription: 'Agradecemos sus comentarios. Si desea recibir información de seguimiento sobre esta encuesta, puede compartir su correo electrónico a continuación.',
    thankYouDescriptionAfterEmail: 'Gracias por compartir su correo electrónico. Le contactaremos más adelante con los resultados de la encuesta y cualquier novedad importante.',
    emailLabel: 'Correo electrónico',
    emailPlaceholder: 'tu@email.com',
    submitButton: 'Enviar',
    submittingButton: 'Enviando...',
    submitSuccess: 'Las actualizaciones por correo quedaron habilitadas. Gracias por su interés.',
    emailAlreadySent: 'Las actualizaciones por correo ya están habilitadas para esta encuesta.',
    submitError: 'No pudimos guardar su correo en este momento. Inténtelo nuevamente.',
    invalidEmail: 'Por favor ingrese un correo electrónico válido',
    restartButton: 'Nueva Encuesta',
    closeMessage: 'Cerrar mensaje',
  },
};

interface ThankYouBlockProps {
  language: Language;
  onSubmit: (email: string) => void;
  onRestart: () => void;
  isSubmitting: boolean;
  submitStatus: SubmitStatus;
  completionMessage: string | null;
  emailAlreadySent: boolean;
}

function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

function ThankYouBlock({
  language,
  onSubmit,
  onRestart,
  isSubmitting,
  submitStatus,
  completionMessage,
  emailAlreadySent,
}: ThankYouBlockProps): React.ReactElement {
  const t = translations[language];
  const [email, setEmail] = useState('');
  const [inputError, setInputError] = useState('');
  const [isSubmitAlertDismissed, setIsSubmitAlertDismissed] = useState(false);
  const [isInputAlertDismissed, setIsInputAlertDismissed] = useState(false);
  const [isCompletionAlertDismissed, setIsCompletionAlertDismissed] = useState(false);

  useEffect(() => {
    setIsSubmitAlertDismissed(false);
  }, [submitStatus, emailAlreadySent]);

  useEffect(() => {
    setIsCompletionAlertDismissed(false);
  }, [completionMessage]);

  const successMessage = submitStatus === 'success' ? t.submitSuccess : emailAlreadySent ? t.emailAlreadySent : '';
  const showEmailForm = !emailAlreadySent && submitStatus !== 'success';
  const descriptionText = emailAlreadySent ? t.thankYouDescriptionAfterEmail : t.thankYouDescription;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setInputError('');

    if (!email.trim()) {
      return;
    }

    if (!validateEmail(email)) {
      setInputError(t.invalidEmail);
      setIsInputAlertDismissed(false);
      return;
    }

    onSubmit(email);
  };

  return (
    <div className="thankyou-block">
      <div className="thankyou-block__content">
        <h2 className="thankyou-block__title">{t.thankYouTitle}</h2>
        <p className="thankyou-block__description">{descriptionText}</p>

        {completionMessage && !isCompletionAlertDismissed && (
          <StatusAlert
            severity="info"
            message={completionMessage}
            closeLabel={t.closeMessage}
            onClose={() => setIsCompletionAlertDismissed(true)}
          />
        )}

        {successMessage && !isSubmitAlertDismissed && (
          <StatusAlert
            severity="success"
            message={successMessage}
            closeLabel={t.closeMessage}
            onClose={() => setIsSubmitAlertDismissed(true)}
          />
        )}

        {submitStatus === 'error' && !isSubmitAlertDismissed && (
          <StatusAlert
            severity="error"
            message={t.submitError}
            closeLabel={t.closeMessage}
            onClose={() => setIsSubmitAlertDismissed(true)}
          />
        )}

        {showEmailForm && (
          <form className="thankyou-block__form" onSubmit={handleSubmit}>
            <div className="thankyou-block__field">
              <label className="thankyou-block__label" htmlFor="email-input">
                {t.emailLabel}
              </label>
              <input
                id="email-input"
                type="email"
                className="thankyou-block__input"
                placeholder={t.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {inputError && !isInputAlertDismissed && (
              <StatusAlert
                severity="warning"
                message={inputError}
                closeLabel={t.closeMessage}
                onClose={() => setIsInputAlertDismissed(true)}
              />
            )}

            <button
              type="submit"
              className="thankyou-block__button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t.submittingButton : t.submitButton}
            </button>
          </form>
        )}

        <button
          className="thankyou-block__button thankyou-block__button--restart"
          onClick={onRestart}
        >
          {t.restartButton}
        </button>
      </div>
    </div>
  );
}

export default ThankYouBlock;
